import Color from 'color'
import Action from '../../actions/bookingCalendar'
import { ReservationFormFieldVisibility } from '../../containers/Form/CreateReservation/withReduxForm'
import { IEvent } from '../../models/Event'
import { IRoom } from '../../models/Room'
import { IService } from '../../models/Services'
import { IDateRange } from '../../utils/Date'
import { CurrencyActions } from '../currency/actionNames'
import { SynchronizeActions } from '../synchronize/actionNames'
import {
  AuthorizationActions,
  UserAccountActions,
} from '../userAccount/actionNames'
import {
  CountryDictionary,
  UserProfileActions,
  UserSettingsActions,
} from '../userObject/actionNames'
import {
  CalendarActions,
  CalendarFiltersActions,
  ConfigurationModalActions,
  DateActions,
  EventActions,
  ReservationModalActions,
  RoomActions,
  RoomModalActions,
  ServicesModal,
} from './actionNames'
import initialStore from './initialState'
import applyFiltersForDate from './mutators/applyFiltersForDate'
import applyFiltersForPlace from './mutators/applyFiltersForPlace'
import calendarIsLoading from './mutators/calendarIsLoading'
import calendarIsReady from './mutators/calendarIsReady'
import changeDateRangeRendered from './mutators/changeDateRangeRendered'
import changeDateRangeScrollable from './mutators/changeDateRangeScrollable'
import changeManualDate from './mutators/changeManualDate'
import clearEventLabels from './mutators/clearEventLabels'
import closeEventHint from './mutators/closeEventHint'
import flushRooms from './mutators/flushRooms'
import mergeEventLabels from './mutators/mergeEventLabels'
import omitEventLabel from './mutators/omitEventLabel'
import requestDataRooms from './mutators/requestDataRooms'
import requestRoomsAccepted from './mutators/requestRoomsAccepted'
import requestRoomsRejected from './mutators/requestRoomsRejected'
import requestUpdateRoomModal from './mutators/requestUpdateRoomModal'
import setFieldsVisibility from './mutators/reservationModal/setFieldsVisibility'
import setSelectedTab from './mutators/reservationModal/setSelectedTab'
import toggleCalculate from './mutators/reservationModal/toggleCalculate'
import toggleCloseModal from './mutators/reservationModal/toggleCloseModal'
import toggleCreateReservationModal from './mutators/reservationModal/toggleCreateReservationModal'
import toggleDeleteModal from './mutators/reservationModal/toggleDeleteModal'
import toggleFieldVisibility from './mutators/reservationModal/toggleFieldVisibility'
import toggleLoading from './mutators/reservationModal/toggleLoading'
import toggleSaving from './mutators/reservationModal/toggleSaving'
import toggleUpdateReservationModal from './mutators/reservationModal/toggleUpdateReservationModal'
import roomWasCreated from './mutators/roomWasCreated'
import roomWasDeleted from './mutators/roomWasDeleted'
import roomWasUpdated from './mutators/roomWasUpdated'
import servicesAccepted from './mutators/ServicesModal/servicesAccepted'
import toggleServicesModal from './mutators/ServicesModal/toggleServicesModal'
import toggleServicesModalLoading from './mutators/ServicesModal/toggleServicesModalLoading'
import setScrollTop from './mutators/setScrollTop'
import showEventHint from './mutators/showEventHint'
import toggleConfigModal from './mutators/toggleConfigModal'
import toggleCreateRoomModal from './mutators/toggleCreateRoomModal'
import toggleRoomModalSaving from './mutators/toggleRoomModalSaving'
import toggleSummary from './mutators/toggleSummary'
import toggleUpdateRoomModal from './mutators/toggleUpdateRoomModal'
import omitEventLabels from './mutators/omitEventLabels'
import { IntegrationAction } from '../airbnb/actionNames'

export default function(
  store: Readonly<IBookingCalendarStore> = initialStore,
  action: Action | any,
): IBookingCalendarStore {
  switch (action.type) {
    case CalendarActions.IsReady:
      return calendarIsReady(store)
    case CalendarActions.IsLoading:
      return calendarIsLoading(store)
    case CalendarActions.ReplaceSelectedColumns:
      return { ...store, selectedColumns: action.columns }

    case DateActions.ChangeDateRangeRendered:
      return changeDateRangeRendered(store, action)
    case DateActions.ChangedManualDate:
      return changeManualDate(store, action)
    case DateActions.ScrollableDateRangeChanged:
      return changeDateRangeScrollable(store, action)

    case EventActions.CloseEventHint:
      return closeEventHint(store)

    case RoomActions.RequestDataForRooms:
      return requestDataRooms(store)
    case RoomActions.DataForRoomsAccepted:
      return requestRoomsAccepted(store, action)
    case RoomActions.DataForRoomsRejected:
      return requestRoomsRejected(store, action)

    case RoomActions.RoomWasCreated:
      return roomWasCreated(store, action)
    case RoomActions.RoomWasUpdated:
      return roomWasUpdated(store, action)
    case RoomActions.RoomWasDeleted:
      return roomWasDeleted(store, action)

    case RoomModalActions.ToggleCreateRoomModal:
      return toggleCreateRoomModal(store)
    case RoomModalActions.ToggleUpdateRoomModal:
      return toggleUpdateRoomModal(store)
    case RoomModalActions.RequestUpdateRoomModal:
      return requestUpdateRoomModal(store, action)
    case RoomModalActions.ToggleSaving:
      return toggleRoomModalSaving(store, action)

    case IntegrationAction.AcceptDeleteListing:
      return {
        ...store,
        rooms: store.rooms.map(r =>
          String(r.id) === String(action.payload.id_room)
            ? { ...r, airbnb_mapping: false }
            : r,
        ),
      }
    case IntegrationAction.AcceptCreateListing:
      return {
        ...store,
        rooms: store.rooms.map(r =>
          String(r.id) === String(action.payload.id_room)
            ? { ...r, airbnb_mapping: true }
            : r,
        ),
      }

    case ReservationModalActions.ToggleCreateReservationModalRedux:
      return toggleCreateReservationModal(store, action)
    case ReservationModalActions.ToggleUpdateReservationModalRedux:
      return toggleUpdateReservationModal(store, action)
    case 'Close reservation form modals':
      return {
        ...store,
        reservationModal: {
          ...store.reservationModal,
          createForm: {
            ...store.reservationModal.createForm,
            isOpen: false,
          },
          updateForm: {
            ...store.reservationModal.updateForm,
            isOpen: false,
          },
        },
      }

    case CalendarFiltersActions.FilterByDate:
      return applyFiltersForDate(store, action)
    case CalendarFiltersActions.FilterByPlace:
      return applyFiltersForPlace(store, action)

    case CalendarActions.CalendarsFetched:
      return {
        ...store,
        calendarId: action.calendarId === 0 ? undefined : action.calendarId,
      }

    case CalendarActions.ToggleOnbordingModal:
      return {
        ...store,
        onboarding: action.payload,
      }

    case CalendarActions.ToggleSurveyModal:
      return {
        ...store,
        survey: action.payload
      }

    case CalendarActions.ToggleSummary:
      return toggleSummary(store, action)
    case 'toggle left sidebar':
      return {
        ...store,
        isSidebarBlock: action.isSidebarBlock,
        isSidebarOpen: action.isSidebarOpen,
      }

    case SynchronizeActions.RoomsFlushed:
      return flushRooms(store)

    case AuthorizationActions.FlushState:
      return { ...initialStore, calendarId: undefined }

    case ServicesModal.ToggleServicesModal:
      return toggleServicesModal(store, action)
    case ReservationModalActions.ToggleCloseModal:
      return toggleCloseModal(store, action)

    case ServicesModal.ServicesAccepted:
      return servicesAccepted(store, action)

    case ServicesModal.ToggleServicesModalLoading:
      return toggleServicesModalLoading(store, action)
    case ConfigurationModalActions.ToggleCreateModal:
      return toggleConfigModal(store, action)

    case ReservationModalActions.ToggleLoading:
      return toggleLoading(store)

    case ReservationModalActions.ToggleCalculate:
      return toggleCalculate(store, action)

    case ReservationModalActions.ToggleFieldVisibility:
      return toggleFieldVisibility(store, action)
    case ReservationModalActions.SetFieldsVisibility:
      return setFieldsVisibility(store, action)

    case ReservationModalActions.SetTab:
      return setSelectedTab(store, action)

    case EventActions.EventWasUpdated:
    case EventActions.ReservationWasTransfered:
      return omitEventLabel(store, action)

    case EventActions.EventWasDeleted:
      return {
        ...store,
        reservationModal: {
          ...store.reservationModal,
          updateForm: {
            id: null,
            isOpen: false,
          },
        },
      }

    case ReservationModalActions.ToggleSaving:
      return toggleSaving(store, action)
    case CalendarActions.SetScrollTop:
      return setScrollTop(store, action)
    case ReservationModalActions.ToggleDeleteModal:
      return toggleDeleteModal(store, action)
    case EventActions.ShowEventHint:
      return showEventHint(store, action)
    case UserProfileActions.DataForUserProfileAccepted:
    case CountryDictionary.DictionaryAccepted:
    case CurrencyActions.RequestForCurrencyIsAccepted:
    case UserSettingsActions.DataForUserSettingsAccepted:
    case UserAccountActions.LanguageSwitched:
      return clearEventLabels(store)
    case EventActions.DataForEventsAccepted:
    case EventActions.EventsBulkCreated:
      return omitEventLabels(store, action)
    case CalendarActions.MergeEventLabels:
      return mergeEventLabels(store, action)
    case CalendarActions.SetScrollDragging:
      return {
        ...store,
        scrollIsDragging: action.isDragging,
      }
    case CalendarActions.startScrolling:
      return {
        ...store,
        scrollIsScrolling: true,
      }
    case CalendarActions.finishScrolling:
      return {
        ...store,
        scrollIsScrolling: false,
      }

    case 'Set calendar size':
      const { size } = store
      size[action.view] = action.size
      return { ...store, size }
  }

  return store
}

export interface IBookingCalendarStore {
  calendarId?: number
  isSidebarOpen?: boolean
  isSidebarBlock?: boolean
  customColors: Color[]
  customReservationColors: Color[]
  eventLabels: {
    [eventId: string]: string[]
  }
  dates: {
    manual: Date | null
    rendered: IDateRange
    requested: IDateRange[]
    loaded: IDateRange[] | 'full'
    scrollable: IDateRange
  }
  deleteReservationModal: {
    isOpen: boolean
    id: string | null
  }
  eventHint: {
    anchorEvent: Element
    open: boolean
    id: IEvent['id']
  }
  filtering: {
    byDate?: {
      from?: Date
      to?: Date
    }
    byPlace?: {
      persons?: number
      name?: string
    }
  }
  reservationModal: {
    selectedTab: number
    loading: boolean
    isSaving: boolean
    calculatingPrice: boolean
    createForm: IModal & { roomId: string; from: Date }
    updateForm: IModal & { id: string | null }
    closeModal: {
      open: boolean
    }
    servicesModal: {
      open: boolean
      isLoading: boolean
      services: IService[]
      rates: any
    }
    configModal: {
      open: boolean
    }
    fieldVisibility: ReservationFormFieldVisibility
  }
  roomModal: {
    createForm: IModal
    updateForm: IModal & { roomId: IRoom[keyof Pick<IRoom, 'id'>] | null }
    isSaving: boolean
  }
  rooms: IRoom[]
  request: {
    isCalendarReady: boolean
    rooms: IRequestState
  }
  scrollTop: number
  scrollIsDragging: boolean
  scrollIsScrolling: boolean
  selectedColumns: Date[]
  size: {
    calendar: number
    settings: number
    forms: number
    profile: number
    showSliders: boolean
  }
  summary: boolean | null
  onboarding: boolean
  survey: boolean
}

interface IRequestState {
  isLoading: boolean
  errMessage: string
  acceptedCount?: number
}

interface IModal {
  isOpen?: boolean
}
